/**
 * Inline date field for the resume canvas (PEL-847).
 *
 * The other canvas fields are free-text contentEditable spans, which is right
 * for prose but wrong for dates: a manual edit could leave the resume holding
 * something like "mmkmkmkmkm - kkkk". This field keeps the same inline look
 * (the resume still reads as a resume) but the value can only be SET from a
 * month/year picker, so what lands in the draft is always a real date.
 *
 * Read-only previews never mount this — the canvas renders plain text unless it
 * is editable and wired to a commit sink.
 */

import {
  MAX_YEAR,
  MIN_YEAR,
  MONTH_LABELS,
  PRESENT,
  clampYear,
  formatResumeDate,
  parseResumeDate,
} from '@/lib/resume-date'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const PANEL_WIDTH = 232
// Rendered height of the panel (year row + month grid + footer). Used only to
// decide whether it still fits below the trigger, so an estimate is enough.
const PANEL_HEIGHT = 190
/** Year the grid opens on when the field holds no readable date. */
const DEFAULT_YEAR = new Date().getFullYear()

export function ResumeDateField({
  value,
  onCommit,
  ariaLabel,
  className,
  locked = false,
  placeholder,
  allowPresent = false,
}: {
  value: string
  /** Fired with the new stored value ("Jan 2020", "2020", "Present" or ""). */
  onCommit: (next: string) => void
  ariaLabel: string
  className?: string | undefined
  /** Provenance marker: this path was edited by the user, not the assistant. */
  locked?: boolean | undefined
  /** Muted hint shown while the field is empty. */
  placeholder?: string | undefined
  /** Offer "Present" (end dates only — a start date is never open-ended). */
  allowPresent?: boolean | undefined
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const parsed = parseResumeDate(value)
  // The year the grid is showing. Seeded from the stored value each time the
  // panel opens (an unparseable or empty value starts on the current year).
  const [browsedYear, setBrowsedYear] = useState(() => clampYear(parsed.year ?? DEFAULT_YEAR))
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // The panel is portaled to <body> so it escapes the canvas: the resume paper
  // is `overflow-hidden` and CSS-scaled, which would clip and shrink an
  // in-flow popover. Position is tracked in viewport coordinates while open,
  // since a portaled element no longer follows the trigger in normal flow.
  useEffect(() => {
    if (!open) return
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      // Right-align to the trigger and keep the panel inside the viewport (date
      // fields sit at the right edge of the paper).
      const left = Math.max(
        8,
        Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8),
      )
      // Flip above the trigger when there's no room below (a date low on the
      // page, or a narrow phone viewport).
      const fitsBelow = rect.bottom + 6 + PANEL_HEIGHT <= window.innerHeight
      const top = fitsBelow ? rect.bottom + 6 : Math.max(8, rect.top - PANEL_HEIGHT - 6)
      setPosition({ top, left })
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open])

  // Close on an outside click or Escape (Escape returns focus to the trigger so
  // keyboard users don't lose their place in the resume).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const commit = (next: string) => {
    setOpen(false)
    if (next !== value) onCommit(next)
  }

  const toggle = () => {
    if (!open) setBrowsedYear(clampYear(parseResumeDate(value).year ?? DEFAULT_YEAR))
    setOpen((wasOpen) => !wasOpen)
  }

  const hint = placeholder ?? 'Add date'

  return (
    <span className="inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className={cn(
          'cursor-pointer rounded-sm text-left outline-none hover:bg-brand-50 focus:bg-brand-50 focus:ring-1 focus:ring-brand-300',
          !value && 'italic text-muted-foreground',
          locked && 'border-b border-dashed border-warning-400/70',
          className,
        )}
      >
        {value || hint}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            // biome-ignore lint/a11y/useSemanticElements: floating popover, not a modal <dialog> element
            role="dialog"
            aria-label={`Pick a date for ${ariaLabel}`}
            style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
            className="fixed z-50 rounded-xl border border-border bg-card p-2 text-foreground/80 shadow-xl shadow-slate-900/10"
          >
            <div className="flex items-center justify-between px-1 pb-1.5">
              <button
                type="button"
                aria-label="Previous year"
                disabled={browsedYear <= MIN_YEAR}
                onClick={() => setBrowsedYear((year) => clampYear(year - 1))}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-foreground">{browsedYear}</span>
              <button
                type="button"
                aria-label="Next year"
                disabled={browsedYear >= MAX_YEAR}
                onClick={() => setBrowsedYear((year) => clampYear(year + 1))}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-1">
              {MONTH_LABELS.map((label, index) => {
                const month = index + 1
                const selected =
                  !parsed.present && parsed.year === browsedYear && parsed.month === month
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => commit(formatResumeDate({ year: browsedYear, month }))}
                    className={cn(
                      'rounded-md px-1 py-1.5 text-xs hover:bg-brand-50 hover:text-brand-700',
                      selected
                        ? 'bg-brand-600 text-white hover:bg-brand-600 hover:text-white'
                        : 'text-foreground/80',
                    )}
                  >
                    {label}
                  </button>
                )
              })}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-border pt-2">
              {/* Year-only is a first-class resume date ("2006 - 2008"). */}
              <button
                type="button"
                onClick={() => commit(String(browsedYear))}
                className={cn(
                  'rounded-md px-2 py-1 text-xs hover:bg-brand-50 hover:text-brand-700',
                  !parsed.present && parsed.year === browsedYear && parsed.month == null
                    ? 'bg-brand-600 text-white hover:bg-brand-600 hover:text-white'
                    : 'text-muted-foreground',
                )}
              >
                {browsedYear} only
              </button>
              {allowPresent && (
                <button
                  type="button"
                  onClick={() => commit(PRESENT)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs hover:bg-brand-50 hover:text-brand-700',
                    parsed.present
                      ? 'bg-brand-600 text-white hover:bg-brand-600 hover:text-white'
                      : 'text-muted-foreground',
                  )}
                >
                  {PRESENT}
                </button>
              )}
              <button
                type="button"
                onClick={() => commit('')}
                className="ml-auto rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                Clear
              </button>
            </div>
          </div>,
          document.body,
        )}
    </span>
  )
}
